import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RequireAdminProps {
  children: ReactNode;
}

/**
 * Route guard for /admin/* pages. Verifies the visitor has an active
 * session AND a row in `user_roles` with role='admin'.
 *
 * If no admin exists yet, the signed-in user can claim the first
 * admin slot via a one-time bootstrap (the DB function refuses to
 * grant a second admin this way — additional admins must be added
 * by an existing admin).
 */
const RequireAdmin = ({ children }: RequireAdminProps) => {
  const location = useLocation();
  const { toast } = useToast();
  const [state, setState] = useState<"loading" | "ok" | "denied" | "unauth">("loading");
  const [claiming, setClaiming] = useState(false);

  const check = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      setState("unauth");
      return;
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", sessionData.session.user.id)
      .eq("role", "admin")
      .maybeSingle();
    if (error || !data) setState("denied");
    else setState("ok");
  };

  useEffect(() => {
    check();
    const { data: sub } = supabase.auth.onAuthStateChange(() => check());
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const { data, error } = await supabase.rpc("claim_first_admin");
      if (error) throw error;
      if (data) {
        toast({ title: "You are now the admin", description: "Welcome." });
        await check();
      } else {
        toast({
          title: "Not available",
          description: "An admin already exists. Ask them to grant you access.",
          variant: "destructive",
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed";
      toast({ title: "Could not claim admin", description: msg, variant: "destructive" });
    } finally {
      setClaiming(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Checking access…</p>
      </div>
    );
  }
  if (state === "unauth") {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }
  if (state === "denied") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl text-foreground mb-3">Admin access required</h1>
          <p className="text-sm text-muted-foreground font-light mb-6">
            If you are the very first admin for this site, claim the role below. Otherwise ask
            an existing admin to grant you access.
          </p>
          <button
            type="button"
            onClick={claim}
            disabled={claiming}
            className="px-7 py-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.3em] rounded-full hover:bg-accent transition-colors duration-500 disabled:opacity-50"
          >
            {claiming ? "Working…" : "Claim first admin"}
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

export default RequireAdmin;

